"use client";

import { cn } from "@/lib/utils";
import { api } from "convex/_generated/api";
import { useMutation } from "convex/react";
import { useCallback, useRef, useState } from "react";
import { Check, Loader2, School, User } from "lucide-react";
import { ANALYTICS_EVENTS, track } from "@/lib/analyticsEvents";

export function TeachingProfile({
  teacherName,
  schoolName,
}: {
  teacherName: string | undefined;
  schoolName: string | undefined;
}) {
  const update = useMutation(api.users.updateTeachingProfile);
  const [t, setT] = useState(teacherName ?? "");
  const [s, setS] = useState(schoolName ?? "");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const completionTrackedRef = useRef(false);

  const dirty =
    t.trim() !== (teacherName ?? "").trim() ||
    s.trim() !== (schoolName ?? "").trim();

  const canSave = dirty && !saving && t.trim().length > 0 && s.trim().length > 0;

  const save = useCallback(async () => {
    if (!t.trim() || !s.trim()) { setError("Please fill in both fields."); return; }
    setError(null);
    setSaving(true);
    try {
      const isFirstCompletion =
        !teacherName?.trim() || !schoolName?.trim();
      await update({ teacherName: t.trim(), schoolName: s.trim() });
      if (isFirstCompletion && !completionTrackedRef.current) {
        completionTrackedRef.current = true;
        track(ANALYTICS_EVENTS.teachingProfileCompleted, {
          isFirstCompletion,
        });
      }
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2500);
    } catch {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [schoolName, teacherName, update, t, s]);

  const inputClass = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-[border-color] placeholder:text-muted-foreground focus:border-primary";

  return (
    <div className="dash-panel-in dash-stagger-1 flex flex-col gap-5 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15">
          <User className="h-4 w-4 text-primary-foreground/70" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-foreground">Teaching profile</h2>
          <p className="text-xs text-muted-foreground">Used on every lesson plan — set once.</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            Your name <span className="text-muted-foreground font-normal">(as it appears on plans)</span>
          </span>
          <input
            value={t}
            onChange={(e) => setT(e.target.value)}
            className={inputClass}
            placeholder="e.g. Jane Doe"
            autoComplete="name"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
            <School className="h-3.5 w-3.5 text-muted-foreground" />
            School name
          </span>
          <input
            value={s}
            onChange={(e) => setS(e.target.value)}
            className={inputClass}
            placeholder="e.g. Lycée Ibn Battouta"
          />
        </label>
      </div>

      {error && (
        <p className="rounded-xl border border-destructive/30 bg-destructive/8 px-3 py-2.5 text-xs text-foreground">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!canSave}
          onClick={() => void save()}
          className={cn(
            "flex h-10 items-center gap-2 rounded-xl px-5 text-sm font-semibold transition-all duration-150",
            canSave
              ? "bg-accent text-accent-foreground hover:opacity-90 active:scale-[0.97]"
              : "cursor-not-allowed bg-muted text-muted-foreground",
          )}
        >
          {saving ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>
          ) : (
            "Save profile"
          )}
        </button>

        {savedFlash && (
          <span className="flex items-center gap-1.5 text-xs font-medium text-success">
            <Check className="h-3.5 w-3.5" />
            Saved
          </span>
        )}

        {!t.trim() || !s.trim() ? (
          <span className="text-xs text-muted-foreground">Both fields required.</span>
        ) : null}
      </div>
    </div>
  );
}
